// core
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';

// libraries
import { CookieService } from 'ngx-cookie-service';

export interface IAnalyticsPayload {
  schema: string;
  source: string;
  channel: string;
  action: string;
  target: string;
  status: number;
  actors?: any[];
  request: {
    query?: {},
    cookies?: {},
    headers?: {}
  };
  data: {};
}
export interface IApiKey {
  token: string;
}

declare global {
  interface Window { analytics: any, dataLayer: any }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  config: any;
  analyticsSessionId: any;
  universalAnalyticsId: any;
  dataFlag: any;
  stepFiveSecs: any;
  stepTenSecs: any;
  stepThirtySecs: any;

  constructor(
    public http: HttpClient,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private cookieService: CookieService,
  ) {
    let config: any = window.sessionStorage.getItem('config');
    if (typeof config === 'string') {
      this.config = JSON.parse(config);
    }

    let dataFlag = window.localStorage.getItem('da');
    this.dataFlag = dataFlag ? JSON.parse(dataFlag) : false;

    if (typeof this.dataFlag !== 'boolean') {
      window.localStorage.setItem('da', 'false');
    }

    window.dataLayer = window.dataLayer || [];
    this.analyticsSessionId = this.cookieService.get('_hat');
    this.universalAnalyticsId = this.cookieService.get('_huat');

    if (!this.analyticsSessionId) {
      this.analyticsSessionId = this.generateQuickGuid();
      this.cookieService.set('_hat', this.analyticsSessionId, { path: '/', secure: true });
    }

    if (!this.universalAnalyticsId) {
      this.universalAnalyticsId = this.generateQuickGuid();
      let expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + (365 * 10));
      this.cookieService.set('_huat', this.universalAnalyticsId, { path: '/', expires: expiryDate, secure: true });
    }
  }

  private getUser() {
    let user: any = window.localStorage.getItem('user');
    if (typeof user === 'string') {
      user = JSON.parse(user);
    }

    return user;
  }

  private isPublicEvent(eventName: string, eventCategory: string, eventLabel: string) {
    // TODO: find out what are the Raptor public events
    return false;
  }

  async trackEvent(eventName: string, eventCategory: string, eventLabel: string, analyticsObject: any, eventValue?: any) {
    const eventFullName = eventName + ' - ' + eventCategory + ' - ' + eventLabel;
    let user = this.getUser();
    let data: any = {
      eventValue: {
        info: eventValue,
        analytics: analyticsObject
      }
    };

    if (!eventName) throw Error('Metrics.trackEvent - missing required field eventName!');
    if (!eventCategory) throw Error('Metrics.trackEvent - missing required field eventCategory!');
    if (!eventLabel) throw Error('Metrics.trackEvent - missing required field eventLabel!');

    data.event = 'Beacon';
    data.eventName = eventName;
    data.eventCategory = eventCategory;
    data.eventLabel = eventLabel;
    data.eventUrl = this.router.url;
    data.userSlug = user ? user.slug : ((analyticsObject && analyticsObject.userSlug) || '');
    data._hat = this.analyticsSessionId;
    data._huat = this.universalAnalyticsId;

    // TODO: Add GTM event tracking if needed
    if (this.isPublicEvent(eventName, eventCategory, eventLabel)) {
      window.dataLayer.push({
        'event': 'trackEvent',
        'eventCategory': eventCategory,
        'eventAction': eventName,
        'eventLabel': eventLabel,
        'eventValue': data.eventValue
      });
    }


    // Segment
    if (typeof window.analytics === 'object' && this.config.segmentID) {
      window.analytics.track(eventFullName, data);
    }

    // Maverick
    this.trackMaverick(data);

    // Debug Logging
    if (this.dataFlag) {
      console.debug('Analytics.trackEvent():', eventFullName, data);
    }
  }

  async trackPage(pageData: { pageTitle: string }) {
    let user = this.getUser();
    var data = {
      event: 'PageView',
      path: window.location.pathname,
      referrer: this.document.referrer,
      search: window.location.search,
      title: pageData.pageTitle,
      url: window.location.href,
      userSlug: user?.slug || '',
      _hat: this.analyticsSessionId,
      _huat: this.universalAnalyticsId
    };

    // GTM
    window.dataLayer.push({
      'event': 'pageview',
      'page': {
        'title': pageData.pageTitle,
        'path': window.location.pathname
      }
    });

    // Maverick
    this.trackMaverick(data);

    // Segment
    if (typeof window.analytics === 'object' && this.config.segmentID) {
      window.analytics.track(data.title, data);
    }

    // Debug Logging
    if (this.dataFlag) {
      console.debug('Analytics.trackPage():', 'PageView', data);
    }
  }

  async trackMaverick(data: any) {
    const user = this.getUser();
    const cookieData = { _hat: data?._hat, _huat: data?._huat };
    let actors = [];
    let url: any;
    let apiKey;

    // console.log('trackMaverick() - ', data);

    // pick out the adword cookies
    const allCookies = this.cookieService.getAll();
    const adwordsCookies = (
      ({ _utm_campaign, _utm_content, _utm_medium, _utm_source, _utm_term}) => (
        { _utm_campaign, _utm_content, _utm_medium, _utm_source, _utm_term}
      )
    )(allCookies);
    // merge cookie data
    Object.assign(cookieData, adwordsCookies);

    if (user && user.slug && user.email) {
      actors.push({
        id: user.slug,
        email: user.email,
        phone: user.mobile,
        role: 'end-user'
      });
    }

    if (this.config) {
      url = this.config.maverickUrl || null;
      apiKey = this.config.maverickApiKey || null;
    }

    const payload: IAnalyticsPayload = {
      schema: 'v5',
      source: window.location.host,
      channel: 'portal',
      action: 'POST',
      target: data?.eventName + '|' + data?.eventCategory + '|' + data?.eventLabel,
      status: 200,
      actors: actors,
      request: {
        cookies: cookieData,
        headers: {
          HTTP_HOST: window.location.host,
          HTTP_ORIGIN: window.location.origin,
          HTTP_REFERER: window.location.href,
          HTTP_USER_AGENT: window.navigator.userAgent
        }
      },
      data: data
    };

    const options = {
      headers: {
        'X-Api-Key': apiKey
      }
    };

    if (url && apiKey && data) {
      return this.http.post(`${url}`, payload, options)
        .toPromise()
        .catch((response: HttpErrorResponse) => {
          console.error('ERROR: Unable to log maverick interaction!', response);
        });
    } else {
      console.log('mavericks url, api key or data was not provided');
      return;
    }
  }

  generateQuickGuid() {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }
}
